import type { ProductPageContent } from "./types";
import { CERBERUS_SITE } from "../nav/navigation";

/**
 * Every number here comes from the checked-in validation harness in the
 * Cerberus repository (`harness/validation`, `docs/research-results.md`) and is
 * stated with the qualifier the report itself carries. Detection figures are
 * from observe-only runs; nothing on this page claims a blocking success rate,
 * because the published evidence set does not measure one.
 */
export const CERBERUS: ProductPageContent = {
  slug: "cerberus",
  proofClaim:
    "Across 525 live attack runs, Cerberus flagged the privileged read and the injected instruction every single time.",
  lede:
    "Cerberus is runtime security for AI agent tool execution. It correlates privileged data access, untrusted content and outbound behavior at the tool-call level, then interrupts the outbound action before it executes.",
  ctas: [
    { label: "See pricing", href: CERBERUS_SITE.pricing, external: true },
    { label: "Read the research", href: "/research" },
  ],
  problem: {
    heading: "Any agent that can read your data, read the web and send a message is already exploitable",
    body:
      "The attack needs no zero-day and no malware. It needs three tool calls the agent was designed to make, and an instruction hidden in content the agent was told to read. Model-level resistance shifts which payloads work; it does not remove the condition.",
    points: [
      {
        title: "1 · Privileged access",
        body: "The agent reads customer records, credentials or internal documents — exactly what you gave it access to do.",
      },
      {
        title: "2 · Injection",
        body: "An attacker embeds instructions in a page, ticket or document the agent fetches as part of the same task.",
      },
      {
        title: "3 · Exfiltration",
        body: "The agent follows the injected instruction and sends your data to the attacker's destination, then reports success.",
      },
    ],
  },
  video: {
    heading: "Watch the attack, and the moment it stops",
    body:
      "One minute, following a single agent session: a privileged read, a poisoned page, and the outbound call that would have sent your customer data to an attacker.",
    title: "Cerberus: 0 bytes left the network",
    duration: "1 min 2 sec",
    src: "/media/cerberus-dramatic.mp4",
    poster: "/media/cerberus-dramatic-poster.jpg",
    captions: "/media/cerberus-dramatic.en.vtt",
    caption:
      "A dramatization of the attack pattern Cerberus is built for, using synthetic data on systems we own. The measured detection figures are in the evidence set below.",
  },
  capabilities: {
    heading: "Detection at the tool boundary, not the prompt",
    lede:
      "Prompt filtering guesses at intent. Cerberus watches what the agent actually does, correlates it across the session, and decides at the last safe moment — the outbound call.",
    items: [
      {
        title: "Layered correlation, not single-signal alerting",
        body: "L1 classifies data sensitivity, L2 tracks token provenance through the context, L3 judges outbound intent and L4 watches memory. A verdict is raised from the combination, so ordinary work does not trip it.",
      },
      {
        title: "Interrupts the action, not the conversation",
        body: "Guarded outbound tool calls are held and blocked when the trifecta closes. The agent keeps working; the byte never leaves.",
      },
      {
        title: "Provenance you can replay",
        body: "Every verdict carries the signals and the contamination graph that produced it, so an incident review is a query rather than an archaeology project.",
      },
      {
        title: "Drops into the stack you already use",
        body: "MIT-licensed core on npm and PyPI, with adapters for LangChain, CrewAI and the Vercel AI SDK, plus OpenTelemetry output.",
      },
      {
        title: "Fails closed",
        body: "If licensing, configuration or the pipeline itself is unavailable, guarded calls stop rather than silently passing through.",
      },
    ],
  },
  evidence: {
    heading: "The evidence set",
    lede:
      "We built a three-tool attack agent, ran 55 injection payloads across six attack categories against three major model providers, and published the traces.",
    stats: [
      {
        value: "525",
        label: "live attack runs",
        note: "55 payloads × 6 categories × 3 providers × 3 trials, plus 30 control runs.",
      },
      {
        value: "90.3%",
        label: "of unprotected runs complied with the injection",
        note: "GPT-4o-mini, 149/165, Wilson 95% CI [84.8%, 93.9%]. Gemini 2.5 Flash: 82.4%.",
      },
      {
        value: "100%",
        label: "L1 and L2 detection across all 525 runs",
        note: "Observe-only mode. L3 fires when an unauthorized outbound call actually executes, so its rate tracks attack success rather than miss rate.",
      },
      {
        value: "0.0%",
        label: "false positives",
        note: "95% CI [0.0%, 11.4%] across treatment and control runs. Real-world rate depends on how trust levels and authorized destinations are configured.",
      },
      {
        value: "52µs",
        label: "median added latency",
        note: "p99 0.23ms — about 0.01% of a typical 600ms model call. Measured against raw tool execution, no network in the loop.",
      },
      {
        value: "0/30",
        label: "control-group exfiltrations",
        note: "Baseline confirmed clean before any treatment run.",
      },
    ],
    methodology:
      "Testing was conducted against systems we own using synthetic PII fixtures. The figures above are the March 2026 three-provider evidence set; later single-provider reruns on the hardened branch are reported separately in the research write-up rather than blended into these numbers.",
    link: { label: "Full methodology and traces", href: "/research" },
  },
  editions: {
    heading: "Editions",
    items: [
      {
        name: "Core",
        price: "MIT licensed",
        blurb: "The detection library. Install it and wrap your tool executor.",
        bullets: [
          "L1–L4 detection pipeline",
          "LangChain, CrewAI and Vercel AI SDK adapters",
          "npm and PyPI packages",
          "OpenTelemetry signals",
        ],
      },
      {
        name: "Enterprise",
        price: "From $12,000/year",
        blurb: "Self-hosted gateway and durable evidence for agents in production.",
        bullets: [
          "Proxy gateway for agents you do not control the code of",
          "Durable verdict ledger and evidence export",
          "Grafana and Prometheus monitoring stack",
          "Deployment tooling and support",
        ],
        cta: { label: "See pricing", href: CERBERUS_SITE.pricing, external: true },
      },
    ],
  },
  services: {
    heading: "Deploy it with the engineers who built it",
    body:
      "Warden runs Cerberus deployments, reviews the tool boundaries you are enforcing, and red-teams the agents behind them.",
    links: [
      { label: "Cerberus Runtime Deployment", href: "/warden/cerberus-deployment" },
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Research", label: "Lethal Trifecta validation report", href: "/research" },
      { kind: "Documentation", label: "Integration and configuration guide", href: "/docs" },
      {
        kind: "Demo",
        label: "Watch a live attack get blocked",
        href: CERBERUS_SITE.demo,
        external: true,
      },
      { kind: "Solution", label: "Secure Agentic AI", href: "/secure-agentic-ai" },
    ],
  },
  close: {
    heading: "Give agents real access without the risk",
    body:
      "Start with the MIT core in your own environment, or have Warden stand the runtime up alongside your team.",
    ctas: [
      { label: "See pricing", href: CERBERUS_SITE.pricing, external: true },
      { label: "Request a briefing", href: "/contact?topic=cerberus" },
    ],
  },
};
